import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { ErrorMessage } from 'formik';
import { jwtDecode } from 'jwt-decode';
import { setActiveTab } from '../../redux/slices/header-slice.js';
import { setNewScObj } from '../../redux/slices/new-sc-slice.js';
import LoadingSpinner from '../../components/common/LoadingSpinner.jsx';
import useNewScs from '../../hooks/useNewScs.js';
import useUsers from '../../hooks/useUsers.js';
import NewScCardInfoContainer from '../../components/new-sc-vetting-cards/NewScCardInfoContainer.jsx';
import OnboardingContainer from '../../components/new-sc-vetting-cards/OnboardingContainer.jsx';
import ScCheckpointTable from './ScCheckpointTable.jsx';
import TableTitleHeader from '../../components/common/TableTitleHeader.jsx';
import { blue } from '../../styles/standard-colors.js';

/**
 * React component for the page displaying a single new sc card and all it's info.
 *
 * @component
 * @returns {JSX.Element} The JSX representation of the ViewNewScCardPage component.
 */
const ViewNewScCardPage = () => {
    const dispatch = useDispatch();

    const { cardId } = useParams();
    const {
        error,
        loading,
        getNewSc,
        getScCheckpointResults,
        checkpointResults,
        loadingCheckpoint,
        checkpointError
    } = useNewScs();
    const { getUser } = useUsers();
    const [userPermissions, setUserPermissions] = useState([]);

    useEffect(() => {
        dispatch(setActiveTab('New SC'));

        return () => {
            dispatch(setActiveTab(null));
        };
    }, [dispatch]);

    useEffect(() => {
        const getUserData = async () => {
            const token = sessionStorage.getItem('accessToken');

            const decoded = jwtDecode(token);

            const returnedUser = await getUser(decoded.sub);

            if (returnedUser) {
                setUserPermissions(returnedUser.role.permissions);
            }
        };

        getUserData();
    }, []);

    useEffect(() => {
        const getData = async () => {
            const returnedNewScCard = await getNewSc(cardId);
            if (returnedNewScCard) {
                dispatch(setNewScObj(returnedNewScCard));
                // 🔹 Fetch checkpoints right after SC is loaded
                await getScCheckpointResults(
                    returnedNewScCard.identifier || cardId
                );
            }
        };

        getData();
    }, [cardId, dispatch, getNewSc, getScCheckpointResults]);

    if (loading)
        return (
            <div style={{ marginTop: '300px' }}>
                <LoadingSpinner />
            </div>
        );
    if (error)
        return (
            <div style={{ marginTop: '300px' }}>
                <ErrorMessage message={error.message} />
            </div>
        );

    return (
        <div className="offices-container">
            <NewScCardInfoContainer />
            <OnboardingContainer userPermissions={userPermissions} />
            <div
                style={{
                    width: '45%',
                    marginBottom: '1.5rem',
                    backgroundColor: '#fff'
                }}>
                <TableTitleHeader
                    title="Module Checkpoints"
                    underlineColor={blue}
                    customContainerStyles={{
                        marginBottom: '1rem',
                        marginLeft: '10px',
                        justifyContent: 'center'
                    }}
                />
                {loadingCheckpoint && <p>Loading checkpoint results...</p>}
                {!loadingCheckpoint &&
                    (checkpointError ||
                        !checkpointResults?.modules?.length) && (
                        <div>
                            <p
                                style={{
                                    fontSize: '0.875rem',
                                    color: '#555',
                                    margin: 0
                                }}>
                                No checkpoint results found.
                            </p>
                        </div>
                    )}
                {!loadingCheckpoint && !checkpointError && (
                    <ScCheckpointTable
                        modules={checkpointResults?.modules || []}
                    />
                )}
            </div>
        </div>
    );
};

export default ViewNewScCardPage;
